import { Link } from "react-router-dom";
import { ArrowRight, Lightbulb, ShieldCheck, Award, Sparkles } from "lucide-react";

const STORY_IMG =
  "https://images.unsplash.com/photo-1774186184398-1cc2da3d029e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxjb3Jwb3JhdGUlMjBleGVjdXRpdmUlMjBtZWV0aW5nJTIwb2ZmaWNlfGVufDB8fHx8MTc3NzMwMjIzOXww&ixlib=rb-4.1.0&q=85";

const VALUES = [
  {
    icon: Lightbulb,
    title: "Innovation",
    body: "We bring AI-enhanced, future-proof thinking to entrenched SAP problems — drawing on broad experience across HANA, S/4, B1 and ByD.",
  },
  {
    icon: ShieldCheck,
    title: "Integrity",
    body: "We work as partners with ownership — not as suppliers. We tell clients what they need to hear, and we stand behind every deliverable.",
  },
  {
    icon: Award,
    title: "Quality",
    body: "Our commitment is high-quality services delivered to spec — measured in value, satisfaction, and long-term system stability.",
  },
  {
    icon: Sparkles,
    title: "Affordability",
    body: "We help clients reach their business objectives through SAP &amp; IT initiatives at the lowest possible cost — without compromising on outcomes.",
  },
];

const About = () => {
  return (
    <div data-testid="about-page">
      {/* PAGE HERO */}
      <section
        data-testid="about-hero"
        className="bg-[#F5F5DC] pt-32 pb-24 lg:pt-40 lg:pb-32"
      >
        <div className="container-pro grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <p className="overline mb-6 fade-up">About Anila Consulting</p>
            <h1 className="font-display text-5xl lg:text-7xl text-[#111111] leading-[1.05] fade-up-delay-1">
              Built for organisations who
              <br />
              <span className="text-[#8B0000]">demand more</span> from an
              SAP consultancy.
            </h1>
            <p className="mt-6 font-display italic text-2xl text-[#8B0000] fade-up-delay-2">
              Empowering Business. Accelerating Growth.
            </p>
          </div>
          <div className="lg:col-span-5 flex items-end fade-up-delay-2">
            <p className="text-lg text-[#4A4A4A] leading-relaxed">
              Anila Consulting is an IT business consulting firm founded in
              2024. We are engaged in a diverse portfolio of SAP services
              that have achieved rapid growth across India's manufacturing,
              retail, FMCG, oil, real estate and infrastructure sectors.
            </p>
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section data-testid="mission-vision-section" className="section bg-white">
        <div className="container-pro grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20">
          <div data-testid="vision-block" className="border-l-2 border-[#8B0000] pl-8">
            <p className="overline mb-4">Our vision</p>
            <h2 className="font-display text-3xl lg:text-4xl text-[#111111] leading-tight mb-5">
              True partners for our clients' end-to-end IT needs.
            </h2>
            <p className="text-[#4A4A4A] leading-relaxed">
              "Our vision is to become true partners of clients for their
              end-to-end IT needs and to be recognised as a solution provider
              into Retail, Manufacturing, Services, FMCG &amp; Oil, Real
              Estate and Infrastructure domains — by helping our clients achieve
              their business objectives through SAP &amp; IT initiatives at
              the lowest possible cost."
            </p>
          </div>
          <div data-testid="mission-block" className="border-l-2 border-[#111111] pl-8">
            <p className="overline mb-4" style={{ color: "#111111" }}>
              Our mission
            </p>
            <h2 className="font-display text-3xl lg:text-4xl text-[#111111] leading-tight mb-5">
              High-quality services in our niche SAP verticals.
            </h2>
            <p className="text-[#4A4A4A] leading-relaxed">
              "Commitment to deliver high-quality services in niche industry
              verticals of SAP HANA/ECC, SAP B1, SAP ByD and Big Data
              Analytics and Automation."
            </p>
            <p className="text-[#4A4A4A] leading-relaxed mt-4">
              Our customer focus is large companies searching for automation
              capabilities. We deliver client needs in terms of value,
              quality, and satisfaction.
            </p>
          </div>
        </div>
      </section>

      {/* STORY */}
      <section
        data-testid="story-section"
        className="section bg-[#F5F5DC]"
      >
        <div className="container-pro grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <img
              src={STORY_IMG}
              alt="Strategic discussion"
              className="w-full h-[520px] object-cover"
            />
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <p className="overline mb-5">Our story</p>
            <h2 className="font-display text-4xl lg:text-5xl text-[#111111] leading-tight">
              Founded in 2024.
              <br />
              <span className="text-[#8B0000]">Built on partnership.</span>
            </h2>
            <span className="divider-red mt-6 mb-6" />
            <div className="space-y-5 text-[#4A4A4A] leading-relaxed">
              <p>
                Anila Consulting was founded in 2024 as an IT business
                consulting firm — one of the rapidly growing names in
                India's SAP services space. Our diverse portfolio of
                engagements has delivered measurable value across retail,
                manufacturing, services, FMCG, oil, real estate and
                infrastructure.
              </p>
              <p>
                We saw a recurring pattern in the industry: SAP consulting
                firms positioned themselves as suppliers rather than
                collaborators, and clients ended up navigating their most
                complex IT programmes alone. We chose to work differently —
                with ownership, with empathy for the operational realities of
                our clients, and with the technical depth to actually solve
                their problems.
              </p>
              <p>
                Today, we are a trusted SAP HANA, ECC, B1, ByD, analytics and
                automation partner — engaged across implementation,
                migration, BI/BO, AMS, mobility, system audits, and GST/Tax
                rollouts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section data-testid="values-section" className="section bg-white">
        <div className="container-pro">
          <div className="max-w-3xl mb-16">
            <p className="overline mb-5">Core values</p>
            <h2 className="font-display text-4xl lg:text-5xl text-[#111111] leading-tight">
              The principles that
              <br />
              <span className="text-[#8B0000]">govern our work.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.title}
                  data-testid={`value-${v.title}`}
                  className="bg-white p-10 lg:p-12 flex gap-6"
                >
                  <Icon
                    size={36}
                    strokeWidth={1.5}
                    className="text-[#8B0000] flex-shrink-0 mt-1"
                  />
                  <div>
                    <h3 className="font-display text-2xl text-[#111111] mb-3">
                      {v.title}
                    </h3>
                    <p
                      className="text-[#4A4A4A] leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: v.body }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CUSTOMER FOCUS — dark charcoal */}
      <section
        data-testid="customer-focus-section"
        className="section bg-[#111111] text-white"
      >
        <div className="container-pro max-w-4xl">
          <p className="overline mb-6">Customer focus</p>
          <h2 className="font-display text-4xl lg:text-5xl leading-tight text-white">
            Our customer focus is large companies searching for automation
            capabilities.
          </h2>
          <p className="text-white/70 text-lg leading-relaxed mt-8">
            We deliver client needs in terms of value, quality, and
            satisfaction — and we work as long-term partners through every
            phase of the engagement: advisory, deployment, upgrades, and
            ongoing support.
          </p>
          <Link
            to="/contact"
            data-testid="about-cta-link"
            className="btn-ghost-light mt-10"
          >
            Speak with our team <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
